import {experimentalStyled as styled} from "@mui/material/styles";
import businessDays from "business-days-js";
import dayjs from "dayjs";
import {sortBy} from "lodash";
import {add, formatCurrency, multiply, subtract} from "../../../helpers";
import {Colors, FormControlLabel} from "../../../ui";
import {PaymentPlanOffer, PaymentPlanOffers} from "../../declarations";

const bDays = businessDays();

const mapFrequencyToTitle: Record<string, {title: string; days: number}> = {
  weekly: {title: "Weekly", days: 7},
  bi_weekly: {title: "Every 2 Weeks", days: 14},
  first_and_fifteenth: {title: "Every 1st & 15th", days: 15},
  monthly: {title: "Monthly", days: 30},
  one_time: {title: "One Time", days: 1},
  last_business_day_of_the_month: {title: "Last business day of the month", days: 30},
};

const mapFrequencyToSuggestionsLabels: Record<string, {label: string; shortLabel: string}> = {
  weekly: {label: "weeks", shortLabel: "wk"},
  bi_weekly: {label: "payments", shortLabel: " 2wk"},
  first_and_fifteenth: {label: "payments", shortLabel: " 1st & 15th"},
  monthly: {label: "months", shortLabel: "mo"},
};

const buildMarks = (planOffersByFrequency: PaymentPlanOffer[]) => {
  return planOffersByFrequency
    .filter((plan) => {
      return !plan.resolution_expert_only;
    })
    .map((plan, index) => {
      const isFirstOrLast = index === 0 || index === planOffersByFrequency.length - 1;
      return {
        label: isFirstOrLast && formatCurrency(plan.installment_size),
        value: index,
      };
    });
};

const StyledFormControlLabel = styled(FormControlLabel)<{selected: boolean}>(({selected}) => ({
  padding: "8px 15px",
  width: "100%",
  minHeight: "63px",
  gap: "12px",
  fontWeight: "600px",
  border: "1px solid",
  borderRadius: "4px",
  borderColor: selected ? Colors.grey._300 : Colors.grey._200,
}));

const nextFirstAndFifteenthDates = ({
  numberOfDays = 30,
  startDate = dayjs(),
}: {
  numberOfDays?: number;
  startDate?: dayjs.Dayjs;
}): dayjs.Dayjs[] => {
  const nextDays: dayjs.Dayjs[] = [];
  [...Array(numberOfDays)].forEach((_, i) => {
    const currentDate = dayjs(startDate).add(i, "day");
    const dayOfMonth = currentDate.date();

    if (dayOfMonth === 1 || dayOfMonth === 15) {
      nextDays.push(currentDate.clone());
    }
  });

  return nextDays;
};

const nextLastBusinessDayOfTheMonthDates = ({
  numberOfMonths = 2,
  startDate = dayjs(),
}: {
  numberOfMonths?: number;
  startDate?: dayjs.Dayjs;
}): dayjs.Dayjs[] => {
  const nextDays: dayjs.Dayjs[] = [];
  [...Array(numberOfMonths)].forEach((_, i) => {
    let lastBusinessDay = dayjs(startDate.add(i, "month")).endOf("month");
    while (!bDays.check(lastBusinessDay.toDate())) {
      lastBusinessDay = lastBusinessDay.subtract(1, "day");
    }
    nextDays.push(lastBusinessDay.clone());
  });

  return nextDays;
};

const getFirstFrequencyWithOptions = (planOffers: PaymentPlanOffers) => {
  if (planOffers) {
    if (planOffers.weekly.length > 0) return "weekly";
    if (planOffers.bi_weekly.length > 0) return "bi_weekly";
    if (planOffers.first_and_fifteenth.length > 0) return "first_and_fifteenth";
    if (planOffers.monthly.length > 0) return "monthly";
  }
  return null;
};

const sortPlansByInstallment = (paymentOffers: PaymentPlanOffer[], direction?: string): PaymentPlanOffer[] => {
  const sortedOffers = sortBy(paymentOffers, (offer) => offer.hardship_payment_plan?.installment_size);
  return direction === "desc" ? sortedOffers.reverse() : sortedOffers;
};

const getLastPaymentAmount = (paymentPlanOffer: PaymentPlanOffer) => {
  if (
    paymentPlanOffer.total_amount ===
      multiply(paymentPlanOffer.number_of_installments, paymentPlanOffer.installment_size) ||
    paymentPlanOffer.data?.fees_added_to_first_installment
  ) {
    return {lastPayment: 0, numOfInstallments: paymentPlanOffer.number_of_installments};
  } else {
    const numOfInstallments = paymentPlanOffer.number_of_installments - 1;
    const hardshipAmount = paymentPlanOffer.hardship_payment_plan
      ? multiply(
          paymentPlanOffer.hardship_payment_plan.number_of_installments,
          paymentPlanOffer.hardship_payment_plan.installment_size
        )
      : 0;
    return {
      lastPayment: subtract(
        paymentPlanOffer.total_amount,
        add(multiply(numOfInstallments, paymentPlanOffer.installment_size), hardshipAmount)
      ),
      numOfInstallments: numOfInstallments,
    };
  }
};

const getTotalAmountLabel = (amountBeforeDiscountCents: number, totalAmount: number) => {
  return amountBeforeDiscountCents !== totalAmount ? "Settlement amount" : "Total amount";
};

const getPlanType = (isSinglePayment: boolean, isHardship: boolean) => {
  if (isSinglePayment) {
    return "one_time_payment";
  }
  if (isHardship) {
    return "hardship_plan";
  }
  return "payment_plan";
};

export {
  mapFrequencyToTitle,
  mapFrequencyToSuggestionsLabels,
  buildMarks,
  StyledFormControlLabel,
  nextFirstAndFifteenthDates,
  nextLastBusinessDayOfTheMonthDates,
  getFirstFrequencyWithOptions,
  sortPlansByInstallment,
  getLastPaymentAmount,
  getTotalAmountLabel,
  getPlanType,
};
